
import React from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminLayoutProps } from "@/types/admin-types";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AdminSidebar from "@/components/admin/AdminSidebar";
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  BookText, 
  UserSquare, 
  BriefcaseBusiness, 
  ScrollText, 
  ChevronRight,
  BarChart,
  HelpCircle
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const AdminLayout = ({ children, activeTab, setActiveTab, userRole }: AdminLayoutProps) => {
  const isAdmin = userRole === 'admin';
  const isEditor = userRole === 'editor';
  const canManageContent = isAdmin || isEditor;
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <div className="flex flex-1">
        {/* Sidebar */}
        <div className="hidden md:block">
          <AdminSidebar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            userRole={userRole} 
          />
        </div>
        
        {/* Main content */}
        <main className="flex-1 p-4 md:p-8">
          {/* Mobile tabs */}
          <div className="md:hidden mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4 flex flex-wrap gap-1 p-1 bg-background border">
                {/* Only show Dashboard for non-editors */}
                {!isEditor && (
                  <>
                    <TabsTrigger 
                      value="dashboard" 
                      className="flex items-center gap-2 bg-corporate-primary-25 text-corporate-primary-100 data-[state=active]:bg-corporate-primary-75 data-[state=active]:text-white"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </TabsTrigger>
                    
                    {/* Divider */}
                    <div className="flex items-center px-2">
                      <Separator orientation="vertical" className="h-5" />
                    </div>
                  </>
                )}
                
                {/* Only show Strategies section for non-editors */}
                {!isEditor && (
                  <>
                    {/* Strategy related */}
                    <div className={cn(
                      "flex items-center gap-1 text-xs text-corporate-secondary-100 font-semibold px-1.5",
                      "hidden sm:flex"
                    )}>
                      <BarChart className="h-3.5 w-3.5" />
                      <span>Strategies:</span>
                    </div>
                    
                    {isAdmin && (
                      <TabsTrigger 
                        value="providers" 
                        className="flex items-center gap-2 hover:bg-corporate-secondary-25 data-[state=active]:bg-corporate-secondary-100 data-[state=active]:text-white"
                      >
                        <Building2 className="h-4 w-4" />
                        <span>Providers</span>
                      </TabsTrigger>
                    )}
                    <TabsTrigger 
                      value="lead-accounts" 
                      className="flex items-center gap-2 hover:bg-corporate-secondary-25 data-[state=active]:bg-corporate-secondary-100 data-[state=active]:text-white"
                    >
                      <BriefcaseBusiness className="h-4 w-4" />
                      <span>Lead Accounts</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="strategies" 
                      className="flex items-center gap-2 hover:bg-corporate-secondary-25 data-[state=active]:bg-corporate-secondary-100 data-[state=active]:text-white"
                    >
                      <ScrollText className="h-4 w-4" />
                      <span>Strategies</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="accounts" 
                      className="flex items-center gap-2 hover:bg-corporate-secondary-25 data-[state=active]:bg-corporate-secondary-100 data-[state=active]:text-white"
                    >
                      <Users className="h-4 w-4" />
                      <span>Accounts</span>
                    </TabsTrigger>
                  </>
                )}
                
                {isAdmin && (
                  <>
                    {/* Divider */}
                    <div className="flex items-center px-2">
                      <Separator orientation="vertical" className="h-5" />
                    </div>
                    
                    {/* Users section - admin only */}
                    <TabsTrigger 
                      value="users" 
                      className="flex items-center gap-2 hover:bg-corporate-primary-25 data-[state=active]:bg-corporate-primary-75 data-[state=active]:text-white"
                    >
                      <UserSquare className="h-4 w-4" />
                      <span>Users</span>
                    </TabsTrigger>
                  </>
                )}
                
                {/* Always show Website Content section for both admin and editor */}
                {canManageContent && (
                  <>
                    {/* Divider - only if we need to separate from previous sections */}
                    {!isEditor && (
                      <div className="flex items-center px-2">
                        <Separator orientation="vertical" className="h-5" />
                      </div>
                    )}
                    
                    {/* Website Content section */}
                    <div className={cn(
                      "flex items-center gap-1 text-xs font-semibold text-corporate-primary-100 bg-corporate-primary-25 rounded-md px-2 py-1",
                      "hidden sm:flex"
                    )}>
                      <BookText className="h-3.5 w-3.5" />
                      <span>Website Content:</span>
                    </div>
                    
                    <TabsTrigger 
                      value="articles" 
                      className="flex items-center gap-2 hover:bg-corporate-primary-25 data-[state=active]:bg-corporate-primary-75 data-[state=active]:text-white"
                    >
                      <ScrollText className="h-4 w-4" />
                      <span>Articles</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="authors" 
                      className="flex items-center gap-2 hover:bg-corporate-primary-25 data-[state=active]:bg-corporate-primary-75 data-[state=active]:text-white"
                    >
                      <UserSquare className="h-4 w-4" />
                      <span>Authors</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="faqs" 
                      className="flex items-center gap-2 hover:bg-corporate-primary-25 data-[state=active]:bg-corporate-primary-75 data-[state=active]:text-white"
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>FAQs</span>
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
            </Tabs>
          </div>
          
          {/* Breadcrumb */}
          <div className="mb-6">
            <div className="flex items-center text-sm text-gray-500">
              <span className="font-medium text-corporate-primary-100">Admin</span>
              <ChevronRight className="h-4 w-4 mx-1" />
              <span className="font-semibold text-corporate-primary-75 capitalize">{activeTab}</span>
            </div>
          </div>
          
          {/* Tab content */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {children}
            </Tabs>
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminLayout;
